import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import api from '@/lib/api'
import { RefreshCw, XCircle, AlertTriangle, Zap } from 'lucide-react'

type Summary = {
  counts: Record<string, number>
  by_type: { run_type: string; avg_ms: number; max_ms: number; n: number }[]
}
type QueueInfo = { ok: boolean; queued?: number; in_progress?: number; error?: string }

type RunRow = {
  id: number; fund_id: string | null; run_type: string | null
  model_name: string | null; status: string | null
  runtime_ms: number | null; started_at: string | null; finished_at: string | null
  error: string | null
}

type EventRow = {
  id: number; report_id: number | null; job_id: string | null
  event: string; message: string | null; created_at: string
}

export default function AdminJobs() {
  const [filterStatus, setFilterStatus] = useState<string>('')

  const sumQ = useQuery<Summary>({
    queryKey: ['jobs-summary'],
    queryFn: () => api.get('/jobs/summary').then(r => r.data),
    refetchInterval: 8_000,
  })
  const queueQ = useQuery<QueueInfo>({
    queryKey: ['jobs-queue-info'],
    queryFn: () => api.get('/jobs/queue-info').then(r => r.data),
    refetchInterval: 5_000,
  })
  const runsQ = useQuery<RunRow[]>({
    queryKey: ['jobs-runs', filterStatus],
    queryFn: () => {
      const p = new URLSearchParams()
      if (filterStatus) p.set('status', filterStatus)
      p.set('limit', '80')
      return api.get(`/risk-stats/runs?${p}`).then(r => r.data)
    },
    refetchInterval: 5_000,
  })
  const eventsQ = useQuery<EventRow[]>({
    queryKey: ['jobs-events'],
    queryFn: () => api.get('/jobs/events?limit=80').then(r => r.data),
    refetchInterval: 5_000,
  })

  const cancel = useMutation<unknown, unknown, number>({
    mutationFn: (runId: number) => api.post(`/jobs/runs/${runId}/cancel`),
    onSuccess: () => {
      runsQ.refetch()
      eventsQ.refetch()
    },
  })

  const fmtMs = (v: number | null | undefined) =>
    v == null ? '—' : v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`
  const fmtDate = (s: string | null | undefined) => s ? new Date(s).toLocaleString() : '—'
  const statusPill = (s: string | null | undefined) => {
    if (s === 'completed') return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30'
    if (s === 'running')   return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30'
    if (s === 'queued')    return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'
    if (s === 'failed')    return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30'
    return 'bg-muted border-border text-muted-foreground'
  }
  const eventTint = (e: string) => {
    if (e === 'queued')   return 'text-amber-600'
    if (e === 'started')  return 'text-blue-600'
    if (e === 'finished') return 'text-green-600'
    if (e === 'failed')   return 'text-red-600'
    return 'text-muted-foreground'
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Job queue · admin</h1>
          <p className="text-muted-foreground">arq worker health, queue depth, recent runs, and cancellation controls.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            sumQ.refetch(); queueQ.refetch(); runsQ.refetch(); eventsQ.refetch()
          }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-muted"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Tile label="Queued"      value={sumQ.data?.counts.queued ?? 0}       icon={<Zap className="h-4 w-4 text-amber-500" />} />
        <Tile label="Running"     value={sumQ.data?.counts.running ?? 0}      icon={<Zap className="h-4 w-4 text-blue-500" />} />
        <Tile label="Completed (7d)" value={sumQ.data?.counts.completed ?? 0} icon={<Zap className="h-4 w-4 text-green-500" />} />
        <Tile label="Failed (7d)" value={sumQ.data?.counts.failed ?? 0}       icon={<AlertTriangle className="h-4 w-4 text-red-500" />} />
      </div>

      {/* Queue depth */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">Redis queue</h2>
          {queueQ.data && !queueQ.data.ok && (
            <span className="text-xs text-destructive">unreachable — {queueQ.data.error}</span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="border border-border rounded p-3">
            <div className="text-xs uppercase text-muted-foreground">In Redis queue</div>
            <div className="font-mono text-xl mt-1">{queueQ.data?.queued ?? '—'}</div>
          </div>
          <div className="border border-border rounded p-3">
            <div className="text-xs uppercase text-muted-foreground">Worker in progress</div>
            <div className="font-mono text-xl mt-1">{queueQ.data?.in_progress ?? '—'}</div>
          </div>
        </div>
      </div>

      {/* Average runtime by run_type */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="font-semibold mb-3">Runtime by run type (last 7 days)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-xs uppercase text-muted-foreground">Type</th>
                <th className="text-right py-2 px-3 text-xs uppercase text-muted-foreground">n</th>
                <th className="text-right py-2 px-3 text-xs uppercase text-muted-foreground">avg</th>
                <th className="text-right py-2 px-3 text-xs uppercase text-muted-foreground">max</th>
              </tr>
            </thead>
            <tbody>
              {(sumQ.data?.by_type ?? []).map(r => (
                <tr key={r.run_type} className="border-b border-border/50">
                  <td className="py-2 px-3 font-mono text-xs">{r.run_type}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{r.n}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{fmtMs(r.avg_ms)}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{fmtMs(r.max_ms)}</td>
                </tr>
              ))}
              {(sumQ.data?.by_type?.length ?? 0) === 0 && (
                <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No completed runs in the last week.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Runs */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Recent runs</h2>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input text-sm">
              <option value="">all</option>
              <option value="queued">queued</option>
              <option value="running">running</option>
              <option value="completed">completed</option>
              <option value="failed">failed</option>
            </select>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card border-b border-border">
                <tr>
                  <th className="text-left py-2 px-2">id</th>
                  <th className="text-left py-2 px-2">type / model</th>
                  <th className="text-left py-2 px-2">fund</th>
                  <th className="text-left py-2 px-2">status</th>
                  <th className="text-right py-2 px-2">runtime</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {(runsQ.data ?? []).map(r => (
                  <tr key={r.id} className="border-b border-border/50">
                    <td className="py-2 px-2 font-mono text-xs">
                      <Link to="/risk-stats/runs" state={{ runId: r.id }} className="hover:underline">{r.id}</Link>
                    </td>
                    <td className="py-2 px-2 text-xs">
                      <div className="font-mono">{r.run_type}</div>
                      <div className="text-muted-foreground">{r.model_name ?? '(multi)'}</div>
                    </td>
                    <td className="py-2 px-2 font-mono text-xs">{r.fund_id ?? '—'}</td>
                    <td className="py-2 px-2">
                      <span className={'text-xs px-2 py-0.5 rounded border ' + statusPill(r.status)}>
                        {r.status ?? '—'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-xs">{fmtMs(r.runtime_ms)}</td>
                    <td className="py-2 px-2 text-right">
                      {(r.status === 'queued' || r.status === 'running') && (
                        <button
                          title="Mark failed (advisory)"
                          onClick={() => cancel.mutate(r.id)}
                          className="text-xs text-destructive hover:underline inline-flex items-center gap-1"
                        >
                          <XCircle className="h-3 w-3" /> cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {(runsQ.data?.length ?? 0) === 0 && (
                  <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No runs.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Event log */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Event log</h2>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card border-b border-border">
                <tr>
                  <th className="text-left py-2 px-2">time</th>
                  <th className="text-left py-2 px-2">run</th>
                  <th className="text-left py-2 px-2">event</th>
                  <th className="text-left py-2 px-2">message</th>
                </tr>
              </thead>
              <tbody>
                {(eventsQ.data ?? []).map(e => (
                  <tr key={e.id} className="border-b border-border/50">
                    <td className="py-2 px-2 font-mono text-[10px] whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    <td className="py-2 px-2 font-mono text-xs">
                      {e.report_id ? <Link to="/risk-stats/runs" className="hover:underline">{e.report_id}</Link> : '—'}
                    </td>
                    <td className={'py-2 px-2 text-xs ' + eventTint(e.event)}>{e.event}</td>
                    <td className="py-2 px-2 text-xs">{e.message ?? '—'}</td>
                  </tr>
                ))}
                {(eventsQ.data?.length ?? 0) === 0 && (
                  <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No events.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

function Tile({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase text-muted-foreground tracking-wider">{label}</div>
        {icon}
      </div>
      <div className="text-2xl font-mono mt-1">{value}</div>
    </div>
  )
}
